import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import AffiliationsTab from "./AffiliationsTab";
import ConnectionsTab from "./ConnectionsTab";
import ActivityTab from "./ActivityTab";
import MentionsTab from "./MentionsTab";
import { getUserConnections } from "../../api/api";
import {
  useInboxSubscription,
  useUserRealtime,
} from "../../context/UserRealtimeContext";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";
import SignedOutPrompt from "../SignedOutPrompt";

/**
 * Notifications area: tab nav + routed panels.
 * @param {Object} props
 * @param {number} props.userId
 * @param {Object} [props.user]
 * @param {function} [props.refreshUser]
 * @returns {JSX.Element}
 */
function NotificationsPage({ userId, user, refreshUser }) {
  const location = useLocation();
  const [connections, setConnections] = useState([]);
  const reduceMotion = useReducedMotion();
  const { pendingConnectionCount, refreshPendingConnections } =
    useUserRealtime();

  const pendingAffiliationsCount =
    user?.affiliations?.filter((aff) => aff.user_id === userId && aff.status === "pending")
      ?.length || 0;
  const affStr = pendingAffiliationsCount > 9 ? "9+" : pendingAffiliationsCount;

  const pendingConnectionsCount = pendingConnectionCount;
  const connStr = pendingConnectionsCount > 9 ? "9+" : pendingConnectionsCount;

  const reloadConnections = useCallback(async () => {
    if (!userId || user?.type !== "user") {
      setConnections([]);
      return;
    }
    const result = await getUserConnections(userId);
    if (result.success && result.data) {
      setConnections(result.data);
    }
  }, [userId, user?.type]);

  useEffect(() => {
    reloadConnections();
  }, [reloadConnections]);

  useInboxSubscription(
    {
      onReconnect: () => {
        reloadConnections();
        refreshPendingConnections();
      },
    },
    Boolean(userId && user?.type === "user")
  );

  // Keep the connections list in sync when a pending request arrives/changes.
  useEffect(() => {
    if (!userId || user?.type !== "user") return;
    reloadConnections();
  }, [pendingConnectionCount, userId, user?.type, reloadConnections]);

  const tabs = [
    { path: "connections", label: "Connections", count: pendingConnectionsCount, countStr: String(connStr) },
    { path: "affiliations", label: "Affiliations", count: pendingAffiliationsCount, countStr: affStr },
    { path: "activity", label: "Activity" },
    { path: "mentions", label: "Mentions" },
  ];

  /**
   * @param {string} path
   * @returns {boolean}
   */
  const isActiveTab = (path) => {
    return (
      location.pathname === `/notifications/${path}` ||
      (location.pathname === "/notifications" && path === "affiliations")
    );
  };

  if (!user) {
    return (
      <SignedOutPrompt
        title="Notifications"
        description="Sign in to view notifications, connection requests, and organization invites."
      />
    );
  }

  if (user.type === "organization") {
    return (
      <SignedOutPrompt
        variant="restricted"
        title="Notifications"
        description="Notifications are for personal accounts only. Organization accounts do not receive connection requests or activity alerts here."
      />
    );
  }

  const pageEnter =
    reduceMotion
      ? {}
      : { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] } };

  return (
    <div className="bg-ui-background pb-8">
      <motion.div className="mx-auto max-w-7xl px-4 py-6 sm:py-8" {...pageEnter}>
        <h1 className="mb-6 text-2xl font-bold tracking-tight text-text-primary sm:mb-8 sm:text-3xl">
          Notifications
        </h1>

        <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
          <aside className="w-full shrink-0 lg:w-60">
            <Card className="overflow-hidden p-1 shadow-profile-sm">
              <nav className="flex flex-col gap-0.5" aria-label="Notification categories">
                {tabs.map((tab) => {
                  const active = isActiveTab(tab.path);
                  return (
                    <Button
                      key={tab.path}
                      asChild
                      variant={active ? "default" : "ghost"}
                      className={cn(
                        "h-auto w-full justify-between gap-2 px-3 py-2.5 text-left font-normal",
                        !active && "text-text-secondary"
                      )}
                    >
                      <Link to={`/notifications/${tab.path}`}>
                        <span className="truncate">{tab.label}</span>
                        {tab.count !== undefined && tab.count > 0 && (
                          <Badge
                            variant="secondary"
                            className={cn(
                              "shrink-0 text-xs",
                              active &&
                                "border-transparent bg-ui-card font-semibold text-accent"
                            )}
                          >
                            {tab.countStr}
                          </Badge>
                        )}
                      </Link>
                    </Button>
                  );
                })}
              </nav>
            </Card>
          </aside>

          <div className="min-w-0 flex-1">
            <Card className="min-h-[12rem] shadow-profile-sm">
              <CardContent className="p-4 sm:p-6">
                <Routes>
                  <Route path="/" element={<Navigate to="/notifications/affiliations" replace />} />
                  <Route
                    path="affiliations"
                    element={
                      <AffiliationsTab userId={userId} user={user} refreshUser={refreshUser} />
                    }
                  />
                  <Route
                    path="connections"
                    element={
                      <ConnectionsTab
                        userId={userId}
                        connections={connections}
                        setConnections={setConnections}
                        refreshUser={refreshUser}
                        onPendingConnectionsChange={refreshPendingConnections}
                      />
                    }
                  />
                  <Route path="activity" element={<ActivityTab />} />
                  <Route path="mentions" element={<MentionsTab userId={userId} />} />
                </Routes>
              </CardContent>
            </Card>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default NotificationsPage;
